<template>
  <div>
    <v-row>
      <v-col cols="12" sm="6" :md="showCost ? 4 : 6">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('totalAssets')"
                 :value="totals.assets"
                 :tooltip="$t('totalAssetsTooltip')" />
      </v-col>
      <v-col v-if="showCost" cols="12" sm="6" md="4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('totalInstallationCost')"
                 :value="totals.installationCost"
                 :formatter="formatCurrency"
                 :tooltip="$t('installationCostTooltip')" />
      </v-col>
      <v-col cols="12" sm="6" :md="showCost ? 4 : 6">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('lifecycleRecords')"
                 :value="totals.items"
                 :tooltip="$t('lifecycleRecordsTooltip')" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" :md="showCost ? 7 : 12">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <InstallationCostDrillChart v-else-if="showCost" />
        <OutcomeDistributionChart v-else />
      </v-col>
      <v-col v-if="showCost" cols="12" md="5">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="400" />
        <OutcomeDistributionChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="showPassRate" cols="12" md="6">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="400" />
        <PassRateTrendChart v-else />
      </v-col>
      <v-col cols="12" :md="showPassRate ? 6 : 12">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="400" />
        <CompletenessChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <AssetsGrid preset="data" />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import KpiCard from '@/components/cards/KpiCard.vue';
import InstallationCostDrillChart from '@/components/charts/InstallationCostDrillChart.vue';
import OutcomeDistributionChart from '@/components/charts/OutcomeDistributionChart.vue';
import PassRateTrendChart from '@/components/charts/PassRateTrendChart.vue';
import CompletenessChart from '@/components/charts/CompletenessChart.vue';
import AssetsGrid from '@/components/grids/AssetsGrid.vue';
import { useKpiQuery, num } from '@/composables/useKpiQuery';
import { formatCurrency } from '@/utils/formatters';

const store = useStore();

const dataLoaded = computed(() => store.getters.getDataLoaded);
const hasAssetField = computed(() => store.getters.hasAssetField);
const showCost = computed(() => hasAssetField.value('installationCost'));
const showPassRate = computed(() => hasAssetField.value('passRate'));

// Installation Cost, never "asset value": the spec is explicit about the label
// and the figure is the recorded cost of installing the asset.
const { totals } = useKpiQuery(
  `
    SELECT
      COUNT(*) AS assets,
      CAST(COALESCE(SUM(installationCost), 0) AS DOUBLE) AS installationCost,
      CAST(COALESCE(SUM(itemCount), 0) AS BIGINT) AS items
    FROM filteredAssets
  `,
  (row) => ({
    assets: num(row.assets),
    installationCost: num(row.installationCost),
    items: num(row.items),
  })
);
</script>
